import client from './client';

export interface HealthFinding {
  id: string;
  severity: 'high' | 'medium' | 'info';
  category: string;
  title: string;
  evidence: string;
  recommendation: string;
}
export interface PlantHealthReport {
  reportId?: string;
  expiresAt?: string;
  expired?: boolean;
  health?: {
    score: number | null; scale: number; version: number; coveragePercent: number;
    dailyCoveragePercent: number; inverterCoveragePercent: number; assessedWeight: number;
    critical: boolean; status: string; method: string;
    components: { key: string; label: string; weight: number; score: number | null; reason: string }[];
  };
  climateComparison?: {
    source: string; previousYearStart: string | null; previousYearEnd: string | null;
    previousYearEnergyKwh: number | null; previousYearDataDays: number; previousYearChangePercent: number | null;
    weatherDays: number; previousYearWeatherDays: number; matchedWeatherDays: number; previousYearMatchedWeatherDays: number;
    averageTemperatureC: number | null; averageCloudPercent: number | null;
    weatherNormalizedChangePercent: number | null; note: string;
  };
  radiationComparison?: {
    status: 'no_data' | 'partial' | 'available';
    actualKwh: number | null;
    expectedKwh: number | null;
    achievementPercent: number | null;
    comparedInverters: number;
    totalInverters: number;
    inverters: { inverterId:number; inverterName:string; sensorId:number; sensorName:string; actualKwh:number; expectedKwh:number; achievementPercent:number; matchedHours:number; matchedDays:number; eligible:boolean }[];
    daily: {date:string;actualKwh:number;expectedKwh:number}[];
    method:string;
    thresholds:{ minimumRadiationWm2:number;maximumGapMinutes:number;matchingWindowMinutes:number;minimumDays:number;minimumHours:number;deficitPercent:number };
  };
  stringComparison?: {
    status: 'no_data' | 'partial' | 'available'; score: number | null;
    configuredStrings: number; comparedStrings: number; comparedInverters: number; totalInverters: number;
    coveragePercent: number; persistentCount: number; method: string;
    strings: {
      inverterId: number; inverterName: string; stringNumber: number; comparedDays: number;
      samples: number; lowDays: number; averageRatio: number | null; deficitPercent: number | null;
      eligible: boolean; persistent: boolean;
    }[];
    thresholds: { minimumRadiationWm2: number; minimumDays: number; deficitPercent: number; persistencePercent: number };
  };
  availabilityComparison?: {
    status: 'no_data' | 'partial' | 'available'; score: number | null;
    daylightMinutes: number; observedMinutes: number; producingMinutes: number; stoppedMinutes: number;
    missingMinutes: number; uncertainMinutes: number; coveragePercent: number; availabilityPercent: number | null;
    comparedInverters: number; totalInverters: number; method: string;
    inverters: {
      inverterId: number; inverterName: string; daylightMinutes: number; observedMinutes: number;
      producingMinutes: number; stoppedMinutes: number; missingMinutes: number; uncertainMinutes: number;
      coveragePercent: number; observedDays: number; availabilityPercent: number | null; eligible: boolean;
    }[];
    events: { inverterId: number; inverterName: string; kind: 'stopped' | 'missing' | 'uncertain'; startAt: string; endAt: string; minutes: number }[];
    thresholds: { minimumRadiationWm2: number; intervalMinutes: number; zeroPowerThresholdW: number; minimumDays: number; minimumCoveragePercent: number; minimumObservedMinutesPerDay: number; stopWarningMinutes: number };
  };
  plant: { uuid: string; name: string; timezone: string };
  period: { days: number; start: string; end: string; previousStart: string };
  generatedAt: string;
  status: 'critical' | 'attention' | 'insufficient_data' | 'no_findings';
  metrics: { totalEnergyKwh: number | null; dailyAverageKwh: number | null; dataDays: number; inverterCount: number; highPriorityCount: number; previousChangePercent: number | null };
  daily: { date: string; energyKwh: number }[];
  inverters: { id: number; name: string; capacityKw: number | null; energyKwh: number; reportedDays: number; lastSeen: string | null; minutesSinceLastSeen: number | null; temperature: number | null }[];
  alarms: { inverterName: string; code: string; name: string; severity: number; daysSeen: number; firstSeen: string; lastSeen: string }[];
  findings: HealthFinding[];
  limitations: string[];
  unavailableSources: string[];
  thresholds: { communicationMinutes: number; temperatureWarningC: number; temperatureHighC: number; peerDeficitPercent: number; minimumPeerCount: number; minimumReportedDays: number };
  ai: { status: 'disabled' | 'completed' | 'unavailable'; text: string; model?: string };
}

export async function fetchPlantHealthReports(uuid: string, days: number, signal?: AbortSignal): Promise<PlantHealthReport[]> {
  return (await client.get<{ reports: PlantHealthReport[] }>(`/powerplants/${uuid}/health-reports`, { params: { days }, signal })).data.reports;
}

export async function analyzePlantHealth(uuid: string, days: number, includeAi: boolean, signal?: AbortSignal, force = false): Promise<PlantHealthReport> {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const response = await client.post<PlantHealthReport>(`/powerplants/${uuid}/health-analysis`, { days, includeAi, force: attempt === 0 && force }, { signal, timeout: 90000 });
    if (response.status !== 202) return response.data;
    await new Promise<void>((resolve, reject) => {
      if (signal?.aborted) { reject(new Error('İstek iptal edildi.')); return; }
      const abort = () => { clearTimeout(timer); reject(new Error('İstek iptal edildi.')); };
      const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 2000);
      signal?.addEventListener('abort', abort, { once: true });
    });
  }
  throw new Error('Rapor hazırlanıyor. Biraz sonra tekrar deneyin.');
}
